import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqItems } from '../../../mocks/faq';

/**
 * Maps blog post ID → IDs of faqItems most relevant to that post's topic.
 * faqItems IDs:
 *  1 = alcance dos fones
 *  2 = canais simultâneos
 *  3 = tempo de instalação
 *  4 = suporte técnico
 *  5 = tradução simultânea
 *  6 = duração bateria
 *  7 = área coberta
 *  8 = gravar palestras
 *  9 = ambientes externos
 */
const POST_FAQ_MAP: Record<number, number[]> = {
  11: [2, 4, 8],  // Treinamentos corporativos → canais, suporte, gravação
  10: [2, 3, 4],  // Conferência corporativa  → canais, instalação, suporte
  9:  [9, 6, 2],  // Team building / silent disco → externo, bateria, canais
  8:  [9, 6, 4],  // Casamentos → externo, bateria, suporte
  7:  [1, 2, 7],  // Tendências 2026 → alcance, canais, área
  6:  [1, 6, 4],  // Locação fones → alcance, bateria, suporte
  5:  [2, 9, 6],  // Silent disco Brasil → canais, externo, bateria
  4:  [5, 2, 3],  // Conferência silenciosa → tradução, canais, instalação
  3:  [1, 2, 5],  // Palestra silenciosa → alcance, canais, tradução
  1:  [5, 2, 1],  // Multilíngue → tradução, canais, alcance
  2:  [1, 6, 9],  // Tendências áudio → alcance, bateria, externo
};

interface RelatedFAQProps {
  postId: number;
  postCategory: string;
}

export default function RelatedFAQ({ postId, postCategory }: RelatedFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const selectedIds = POST_FAQ_MAP[postId] ?? [1, 2, 4];
  const items = selectedIds
    .map(id => faqItems.find(f => f.id === id))
    .filter(Boolean) as typeof faqItems;

  if (items.length === 0) return null;

  return (
    <aside className="mt-14 bg-gray-50 dark:bg-gray-800/60 rounded-3xl p-8 border border-gray-100 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary/10">
            <i className="ri-question-answer-line text-primary text-xl" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-xl text-secondary dark:text-white leading-tight">
              Perguntas frequentes
            </h3>
            <p className="font-body text-xs text-gray-500 dark:text-gray-400">sobre {postCategory.toLowerCase()}</p>
          </div>
        </div>
        <Link
          to="/faq"
          className="font-body text-xs font-semibold text-primary flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap cursor-pointer"
        >
          Ver todas
          <i className="ri-arrow-right-line text-xs" />
        </Link>
      </div>

      {/* FAQ accordion */}
      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={item.id}
            className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700"
          >
            <button
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer group"
            >
              <span className="font-body text-sm font-semibold text-secondary dark:text-white group-hover:text-primary dark:group-hover:text-primary transition-colors pr-4 leading-snug">
                {item.question}
              </span>
              <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${openIndex === i ? 'bg-primary rotate-45' : 'bg-gray-100 dark:bg-gray-700'}`}>
                <i className={`ri-add-line text-sm ${openIndex === i ? 'text-white' : 'text-gray-500'}`} />
              </div>
            </button>

            {openIndex === i && (
              <div className="px-6 pb-5 border-t border-gray-100 dark:border-gray-700">
                <p className="font-body text-sm text-gray-600 dark:text-gray-300 leading-relaxed pt-4">
                  {item.answer}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer link */}
      <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
        <p className="font-body text-xs text-gray-400 dark:text-gray-500">
          Tem outra dúvida?
        </p>
        <Link
          to="/faq"
          className="inline-flex items-center gap-2 text-xs font-semibold text-primary bg-primary/8 hover:bg-primary/15 px-4 py-2 rounded-full transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-questionnaire-line text-sm" />
          Central de perguntas frequentes
        </Link>
      </div>
    </aside>
  );
}
